import {
  AcceptTermsCheckbox,
  AsyncActionButton,
  ExpandableSection,
  FilteredCreditApplication,
  FinancingConfigDto,
  FormatCurrencyToNearestLowerMultipleToNoDecimals,
  FormatCurrencyToNearestUpperMultipleToNoDecimals,
  Loader,
  SinInput,
  useMerchantFinancingConfig,
  useSubmitHardHit,
} from '@applyonline/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram } from '@if/core/types'
import { formatCurrencyRoundedUpToNoDecimals } from '@if/core/utils'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import {
  getMaxPaymentForRequestedAmount,
  getMinPaymentForRequestedAmount,
  getPrequalifiedAmount,
  mustAskApplicantSIN,
} from '../../../../data/creditapp-selectors'
import { useTrackStepReachedEvent } from '../../../../services/trackingEvents'
import { EditSinAndLoanAmount, buildEditSinAndLoanAmountSchema } from '../../../../types/Schemas/PrequalificationSchema'
import { ETrackingEvent } from '../../../../types/tracking'

interface WrapperProps {
  creditApp: FilteredCreditApplication
}

const ApprovedPrequalFormWrapper = ({ creditApp }: WrapperProps) => {
  const [financingProgramConfig, isFetchingConfig] = useMerchantFinancingConfig(
    creditApp.merchantId,
    creditApp.financingProgramId,
  )

  return isFetchingConfig ? (
    <Loader description="" />
  ) : (
    <ApprovedPrequalForm creditApp={creditApp} financingProgramConfig={financingProgramConfig!} />
  )
}

interface Props {
  creditApp: FilteredCreditApplication
  financingProgramConfig: FinancingConfigDto
}

const ApprovedPrequalForm = ({ creditApp, financingProgramConfig }: Props) => {
  const { t } = useTranslation()
  const initialPrequalifiedAmount = getPrequalifiedAmount(creditApp)
  const minPaymentForInitialApprovedAmount = getMinPaymentForRequestedAmount(
    creditApp,
    financingProgramConfig.maxLoanTerm,
  )
  const maxPaymentForInitialApprovedAmount = getMaxPaymentForRequestedAmount(
    creditApp,
    financingProgramConfig.maxLoanTerm,
  )
  const askApplicantSIN = mustAskApplicantSIN(creditApp)

  useTrackStepReachedEvent(creditApp, ETrackingEvent.PrequalSuccess)

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<EditSinAndLoanAmount>({
    mode: 'onBlur',
    defaultValues: {
      needsApplicantSin: askApplicantSIN,
      requestedLoanAmount: initialPrequalifiedAmount,
      id: creditApp.id,
    },
    resolver: yupResolver(
      buildEditSinAndLoanAmountSchema(
        financingProgramConfig.minimumLoanAmount,
        creditApp.prequalificationDecision.maxLoanAmount,
      ),
    ),
  })

  const [submitHardHit, isSubmitting] = useSubmitHardHit(EFinancingProgram.B2c)
  const innerSubmit = useCallback(
    async (data: EditSinAndLoanAmount) => {
      await submitHardHit(data)
    },
    [submitHardHit],
  )

  return (
    <main className="general-message" style={{ display: 'block' }}>
      <form onSubmit={handleSubmit(innerSubmit)}>
        <h1 className="h3" style={{ lineHeight: 1.5 }}>
          <Trans
            i18nKey="prequalification.prequalified"
            values={{
              requestedAmount: formatCurrencyRoundedUpToNoDecimals(initialPrequalifiedAmount),
              minMonthlyPayment: FormatCurrencyToNearestLowerMultipleToNoDecimals(
                minPaymentForInitialApprovedAmount,
                5,
              ),
              maxMonthlyPayment: FormatCurrencyToNearestUpperMultipleToNoDecimals(
                maxPaymentForInitialApprovedAmount,
                5,
              ),
            }}
          />
        </h1>
        <div className="paragraph">
          <div className="information">
            <Trans i18nKey="prequalification.loanDetails.mainDetails" />
          </div>
          <ExpandableSection toggleText="prequalification.loanDetails.learnMore">
            <p
              style={{
                fontStyle: 'italic',
                border: '1px solid #08abf0',
                borderRadius: '1.5rem',
                textAlign: 'center',
                padding: '1.5rem',
              }}
            >
              {t('prequalification.loanDetails.learnMoreDetails')}
            </p>
          </ExpandableSection>

          {askApplicantSIN && (
            <SinInput
              mandatory
              error={errors.applicantSin}
              id="applicantSin"
              reason="prequalification.applicantNewToCredit"
              {...register('applicantSin')}
            />
          )}

          <div style={{ marginTop: '32px' }}>{t('prequalification.pullCreditReport')}</div>
          <AcceptTermsCheckbox
            id="acceptConditions"
            termId={t('prequalification.acceptConditions')}
            msgOnInvalid={t('common.acceptConditions')}
          />
        </div>

        <div className="btn-group mobile-reversed justify-end">
          <AsyncActionButton type="submit" isPending={isSubmitting} primary style={{ justifyContent: 'center' }}>
            <span>{t('prequalification.continue')}</span>
            <i className="fa-regular fa-arrow-right" />
          </AsyncActionButton>
        </div>

        <div style={{ borderTop: '1px solid black', marginTop: '5rem' }}>
          <p style={{ marginTop: '2rem' }}>
            <Trans
              i18nKey="prequalification.priorToQualifyNotes.1"
              values={{
                minInterestRate: creditApp.prequalificationDecision.minInterestRate,
                ageConditionalTerm: financingProgramConfig.maxLoanTerm,
              }}
            />
          </p>
          <p>{t('prequalification.priorToQualifyNotes.estimate')}</p>
          <p style={{ marginTop: '2rem' }}>{t('prequalification.priorToQualifyNotes.2')}</p>
        </div>
      </form>
    </main>
  )
}

export default ApprovedPrequalFormWrapper
